import React, { useState } from 'react';
import clsx from 'clsx';
import { Field } from 'redux-form';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  FormControl,
  Input,
  InputAdornment,
  InputLabel
} from '@material-ui/core';

import { translationsSelector } from '../../../selectors/translations';

import { CustomIcon, Translation } from '../index';

const useStyles = makeStyles(theme => ({
  root: {
    padding: `0 24px`,
    background: theme.palette.background.default,
    borderRadius: 32,
    boxSizing: 'border-box',
    transition: theme.transitions.custom.medium,
    border: `1px solid ${theme.palette.text.primary}`
  },
  rootFocus: {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.customShadows.module
  },
  label: {
    zIndex: 1,
    left: 24,
    color: theme.palette.text.hint,

    '&$labelShrink': {
      transform: `translate(0, 8px) scale(0.75)`,
      color: theme.palette.text.primary
    }
  },
  labelShrink: {},
  labelWithIcon: {
    left: 56
  },
  inputRoot: {
    marginTop: '15px !important',
    marginBottom: 15
  },
  input: {
    color: theme.palette.text.primary,
    padding: '7px 0 6px',

    '&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus,&:-webkit-autofill:active,&:-internal-autofill-selected':
      {
        animationName: 'none',
        WebkitBoxShadow: '0 0 0 20px white inset'
      },
    '&::placeholder': {
      color: theme.palette.text.hint,
      lineHeight: 'normal'
    },
    '&::-moz-placeholder': {
      color: theme.palette.text.hint
    },
    '&:-ms-input-placeholder': {
      color: theme.palette.text.hint
    }
  },
  inputWithIcon: {
    marginLeft: 8
  },
  warningDark: {
    borderColor: theme.palette.warning.dark
  }
}));

// startAdornment/icon will always make label fixed automatically
const TextInput = React.forwardRef(
  (
    {
      className,
      inputClassName,
      labelClassName,
      startIconClassName,
      id,
      name,
      type = 'text',
      color = 'primary',
      value,
      placeholder,
      inputPlaceholder,
      required,
      onChange,
      onEnterPress,
      onKeyPress,
      onFocus,
      onBlur,
      button,
      startIcon,
      startIconVariant = 'primary',
      labelFixed,
      input,
      inputMode,
      pattern
    },
    ref
  ) => {
    const classes = useStyles();
    const translations = useSelector(translationsSelector);
    const [focus, setFocus] = useState(false);

    const handleKeyPress = e => {
      if (!!onEnterPress && e.key === 'Enter') {
        onEnterPress();
      } else if (onKeyPress) {
        onKeyPress(e);
      }
    };

    const handleFocus = event => {
      setFocus(true);
      input && input.onFocus && input.onFocus(event);
      onFocus && onFocus(event);
    };

    const handleBlur = event => {
      setFocus(false);
      input && input.onBlur && input.onBlur(event);
      onBlur && onBlur(event);
    };

    return (
      <FormControl
        className={clsx(
          classes.root,
          focus && classes.rootFocus,
          classes[color],
          className
        )}
      >
        {placeholder && (
          <InputLabel
            classes={{
              root: clsx(
                classes.label,
                !!startIcon && classes.labelWithIcon,
                labelClassName
              ),
              shrink: classes.labelShrink
            }}
            htmlFor={id}
            shrink={!!(labelFixed || focus || (input && input.value) || value)}
          >
            <Translation keyIfNoValue isFragment>
              {placeholder}
            </Translation>
          </InputLabel>
        )}
        <Input
          disableUnderline
          ref={ref}
          id={id}
          name={name}
          type={type}
          color={color}
          classes={{
            root: clsx(classes.inputRoot, inputClassName),
            input: clsx(classes.input, !!startIcon && classes.inputWithIcon)
          }}
          value={value}
          required={required}
          onChange={onChange}
          onKeyPress={handleKeyPress}
          placeholder={
            inputPlaceholder && translations[inputPlaceholder]
              ? translations[inputPlaceholder]
              : inputPlaceholder
          }
          startAdornment={
            !!startIcon && (
              <CustomIcon
                className={startIconClassName}
                icon={startIcon}
                size="small"
                variant={startIconVariant}
              />
            )
          }
          endAdornment={
            !!button && <InputAdornment position="end">{button}</InputAdornment>
          }
          {...input}
          inputProps={{ pattern }}
          inputMode={inputMode}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </FormControl>
    );
  }
);

const FormTextInput = React.forwardRef(
  ({ onChange, onFormat, name, validate, ...props }, ref) =>
    name ? (
      <Field
        component={TextInput}
        name={name}
        validate={validate}
        onChange={onChange}
        format={onFormat}
        props={{ ...props, ref }}
      />
    ) : (
      <TextInput {...props} {...{ ref, onChange }} />
    )
);

export default FormTextInput;
